import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { UpdateCertificateCriteriaAction } from "../../Redux/Actions/RewardAction";

interface EditCertificateCriteriaModalProps {
  open: boolean;
  onClose: () => void;
  criteria: any;
  onSuccess?: () => void;
}

const EditCertificateCriteriaModal: React.FC<
  EditCertificateCriteriaModalProps
> = ({ open, onClose, criteria, onSuccess }) => {
  const dispatch = useDispatch<any>();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    criteria_type: "assessment",
    level: "completion",
    assessment_id: "",
    score: "",
    is_active: true,
  });

  useEffect(() => {
    if (criteria) {
      const assessmentId =
        criteria?.criteria_json?.[criteria?.criteria_type]?.[criteria?.level]
          ?.assessment_id || "";
      const existingScore =
        criteria?.criteria_json?.[criteria?.criteria_type]?.[criteria?.level]
          ?.score || "";

      setFormData({
        name: criteria?.name || "",
        criteria_type: criteria?.criteria_type || "assessment",
        level: criteria?.level || "completion",
        assessment_id: assessmentId?.toString(),
        score: existingScore?.toString(),
        is_active: criteria?.is_active ?? true,
      });
    }
  }, [criteria]);

  const handleChange = (e: any) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const isExcellence = formData?.level === "excellence";
      const levelData: any = {
        assessment_id: parseInt(formData?.assessment_id),
      };

      if (isExcellence) {
        levelData.score = parseInt(formData?.score);
      }

      const criteriaJson = {
        [formData?.criteria_type]: {
          [formData?.level]: levelData,
        },
      };

      const criteriaData = {
        name: formData?.name,
        criteria_type: formData?.criteria_type,
        level: formData?.level,
        criteria_json: criteriaJson,
        is_active: formData?.is_active,
      };

      const result = await dispatch(
        UpdateCertificateCriteriaAction(criteria?.id, criteriaData)
      );
      if (result && result.status === true) {
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Certificate Award Criteria</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <TextField
            name="name"
            label="Name"
            value={formData?.name}
            onChange={handleChange}
            fullWidth
            required
            placeholder="Enter criteria name"
          />

          <FormControl fullWidth required>
            <InputLabel>Criteria Type</InputLabel>
            <Select
              name="criteria_type"
              value={formData?.criteria_type}
              onChange={handleChange}
              label="Criteria Type"
            >
              <MenuItem value="assessment">Assessment</MenuItem>
              <MenuItem value="contest">Contest</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth required>
            <InputLabel>Level</InputLabel>
            <Select
              name="level"
              value={formData?.level}
              onChange={handleChange}
              label="Level"
            >
              <MenuItem value="participation">Participation</MenuItem>
              <MenuItem value="completion">Completion</MenuItem>
              <MenuItem value="excellence">Excellence</MenuItem>
            </Select>
          </FormControl>
          
          {formData?.level === "excellence" && (
            <TextField
              name="score"
              label="Score"
              value={formData?.score}
              onChange={handleChange}
              fullWidth
              required
              type="number"
              placeholder="Enter minimum score"
            />
          )}

          <TextField
            name="assessment_id"
            label={`${
              formData?.criteria_type === "assessment"
                ? "Assessment"
                : "Contest"
            } ID`}
            value={formData?.assessment_id}
            onChange={handleChange}
            fullWidth
            required
            type="number"
            placeholder="Enter the ID"
          />

          <FormControlLabel
            control={
              <Switch
                name="is_active"
                checked={formData?.is_active}
                onChange={handleChange}
              />
            }
            label="Active"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading ||
            !formData?.assessment_id ||
            !formData?.name ||
            (formData?.level === "excellence" && !formData?.score)
          }
          sx={{
            backgroundColor: "#0555c8",
            "&:hover": {
              backgroundColor: "#043c99",
            },
          }}
        >
          {loading ? <CircularProgress size={24} /> : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCertificateCriteriaModal;
